import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FileText, Filter, Calendar as CalendarIcon, ExternalLink, Building2, ChevronDown, ChevronRight, Search, Trash2, MapPin, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import ConfirmDelete from "@/components/ConfirmDelete";
import RegionSelect from "@/components/RegionSelect";
import RegionFilter from "@/components/RegionFilter";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import { STATUS_STYLES, RevBadge, DownloadPdfButton, QuoteAmount, saleValue } from "@/components/quotation/QuotationBits";
import { can } from "@/lib/perms";
import { QUOTE_TYPES, isRental } from "@/components/quotation/OptionCard";

const LineCode = ({ it, testId }) => {
  if (!it.catalog_id) return null;
  return it.item_code
    ? <div className="text-[10px] text-slate-500 mt-0.5 font-mono" data-testid={testId}>Item code: {it.item_code}</div>
    : <div className="text-[10px] text-amber-600 mt-0.5" data-testid={testId}>Set item code in Machine Catalog</div>;
};

const STATUSES = ["Draft", "Sent", "Opened", "Approved", "Rejected", "Expired"];

function fmtInr(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

export default function Quotations() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = can(user, "quotations", "admin");
  const canCreate = can(user, "quotations", "edit");
  const [params, setParams] = useSearchParams();
  const presetLeadId = params.get("lead") || "";
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [view, setView] = useState("byclient");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(!!presetLeadId);
  const [leads, setLeads] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [collapsed, setCollapsed] = useState({});  // { [company_name]: true } → collapsed
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const parts = [];
      if (filter) parts.push(`status=${encodeURIComponent(filter)}`);
      if (regionFilter && regionFilter !== "__none__") parts.push(`region_id=${encodeURIComponent(regionFilter)}`);
      const params = parts.length ? `?${parts.join("&")}` : "";
      const [q, l, c] = await Promise.all([api.get(`/quotations${params}`), api.get("/leads"), api.get("/catalog")]);
      const data = regionFilter === "__none__" ? q.data.filter((r) => !r.region_id) : q.data;
      setRows(data);
      setLeads(l.data);
      setCatalog(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, regionFilter]);

  // Group by client (falls back to the lead's company_name). Each group is
  // sorted newest-first and annotated with counts + approved value.
  const grouped = useMemo(() => {
    const s = search.trim().toLowerCase();
    const buckets = new Map();
    for (const q of rows) {
      const key = (q.lead?.company_name || "Unassigned").trim() || "Unassigned";
      if (s && !key.toLowerCase().includes(s) && !(q.quotation_number || "").toLowerCase().includes(s)) continue;
      if (!buckets.has(key)) buckets.set(key, { name: key, contact: q.lead?.contact_person || "", quotations: [], total_value: 0, approved_value: 0, latest_at: "" });
      const b = buckets.get(key);
      b.quotations.push(q);
      b.total_value += saleValue(q);
      if (q.status === "Approved") b.approved_value += saleValue(q);
      const at = q.sent_at || q.created_at || "";
      if (at > b.latest_at) b.latest_at = at;
      if (!b.contact && q.lead?.contact_person) b.contact = q.lead.contact_person;
    }
    return Array.from(buckets.values())
      .map((b) => ({ ...b, quotations: [...b.quotations].sort((a, c) => (c.created_at || "").localeCompare(a.created_at || "")) }))
      .sort((a, c) => (c.latest_at || "").localeCompare(a.latest_at || ""));
  }, [rows, search]);

  const toggle = (name) => setCollapsed((p) => ({ ...p, [name]: !p[name] }));

  return (
    <div data-testid="quotations-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Sales</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Quotations</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">Client-wise history — every offer you've drafted, sent, and closed grouped under the account.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          {canCreate && <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 w-full md:w-auto" data-testid="new-quotation-btn"><Plus className="w-4 h-4 mr-1.5" /> New quotation</Button>
          </DialogTrigger>}
          {createOpen && <CreateQuotationDialog leads={leads} catalog={catalog} presetLeadId={presetLeadId} onCreated={(q) => { setCreateOpen(false); setParams({}); nav(`/quotations/${q.id}`); }} onClose={() => { setCreateOpen(false); setParams({}); }} />}
        </Dialog>
      </div>

      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] md:max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search client or quotation number…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="quotation-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400 shrink-0" />
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-44" data-testid="filter-status"><SelectValue placeholder="Filter by status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <RegionFilter value={regionFilter} onChange={setRegionFilter} testId="quotations-region-filter" />
        <div className="flex bg-white border border-slate-200 rounded-md p-0.5">
          <button onClick={() => setView("byclient")} data-testid="view-byclient-btn" className={`px-3 py-1.5 text-xs font-medium rounded transition-colors duration-150 flex items-center gap-1.5 ${view === "byclient" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> By Client
          </button>
          <button onClick={() => setView("byregion")} data-testid="view-byregion-btn" className={`px-3 py-1.5 text-xs font-medium rounded transition-colors duration-150 flex items-center gap-1.5 ${view === "byregion" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
            <MapPin className="w-3.5 h-3.5" /> By Region
          </button>
        </div>
        <span className="text-xs text-slate-500 md:ml-auto">{grouped.length} client{grouped.length === 1 ? "" : "s"} · {rows.length} quotation{rows.length === 1 ? "" : "s"}</span>
      </div>

      {grouped.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg py-14 text-center text-slate-400" data-testid="quotations-empty">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          {rows.length === 0 ? "No quotations yet — create your first one to see it here." : "No client matches your search."}
        </div>
      ) : view === "byregion" ? (
        <QuotationsByRegion rows={rows} onOpen={(q) => nav(`/quotations/${q.id}`)} STATUS_STYLES={STATUS_STYLES} />
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => {
            const open = !collapsed[g.name];
            return (
              <section key={g.name} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`client-group-${g.name}`}>
                <button type="button" onClick={() => toggle(g.name)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`client-toggle-${g.name}`}>
                  {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                  <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
                  <div className="text-left flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{g.name}</div>
                    {g.contact && <div className="text-xs text-slate-500 truncate">{g.contact}</div>}
                  </div>
                  <div className="text-right text-xs text-slate-500 hidden md:flex items-center gap-4">
                    <span>{g.quotations.length} quotation{g.quotations.length === 1 ? "" : "s"}</span>
                    {g.approved_value > 0 && <span className="text-emerald-600 font-medium">{fmtInr(g.approved_value)} won</span>}
                    <span className="font-semibold text-slate-900">{fmtInr(g.total_value)} total</span>
                  </div>
                  <div className="text-right md:hidden">
                    <div className="text-xs font-semibold text-slate-900">{fmtInr(g.total_value)}</div>
                    <div className="text-[11px] text-slate-500">{g.quotations.length} quote{g.quotations.length === 1 ? "" : "s"}</div>
                  </div>
                </button>
                {open && (
                  <>
                    {/* Desktop table */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/60">
                            <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Number</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Total</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Valid until</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Created</TableHead>
                            <TableHead />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {g.quotations.map((q) => (
                            <TableRow key={q.id} onClick={() => nav(`/quotations/${q.id}`)} className="hover:bg-slate-50 cursor-pointer" data-testid={`quotation-row-${q.id}`}>
                              <TableCell className="font-mono text-sm text-slate-900"><span className="inline-flex items-center gap-1.5">{q.quotation_number}<RevBadge revision={q.revision} testId={`quotation-rev-badge-${q.id}`} /></span></TableCell>
                              <TableCell><Badge variant="outline" className={STATUS_STYLES[q.status]}>{q.status}</Badge></TableCell>
                              <TableCell className="text-right font-semibold text-slate-900"><QuoteAmount q={q} fmt={fmtInr} testId={`quotation-amount-${q.id}`} /></TableCell>
                              <TableCell className="text-sm text-slate-600"><CalendarIcon className="w-3 h-3 inline mr-1 text-slate-400" />{formatDMY(q.valid_until)}</TableCell>
                              <TableCell className="text-xs text-slate-500">{formatDMY(q.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <DownloadPdfButton quotation={q} compact testId={`quotation-download-btn-${q.id}`} />
                                  {isAdmin && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(q); }}
                                      className="p-1 text-slate-300 hover:text-rose-600"
                                      data-testid={`quotation-delete-btn-${q.id}`}
                                      title="Delete quotation"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {/* Mobile card list */}
                    <ul className="md:hidden divide-y divide-slate-100 border-t border-slate-100">
                      {g.quotations.map((q) => (
                        <li
                          key={q.id}
                          onClick={() => nav(`/quotations/${q.id}`)}
                          className="px-4 py-3 active:bg-slate-50 flex items-start gap-3"
                          data-testid={`quotation-card-${q.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-slate-900 truncate">{q.quotation_number}</span>
                              <RevBadge revision={q.revision} testId={`quotation-rev-badge-m-${q.id}`} />
                              <Badge variant="outline" className={`${STATUS_STYLES[q.status]} shrink-0 text-[10px]`}>{q.status}</Badge>
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                              <span><CalendarIcon className="w-3 h-3 inline mr-0.5" />valid {formatDMY(q.valid_until)}</span>
                              <span>· {formatDMY(q.created_at)}</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-semibold text-slate-900"><QuoteAmount q={q} fmt={fmtInr} testId={`quotation-amount-m-${q.id}`} /></div>
                            <div className="flex items-center justify-end gap-2 mt-1">
                              <DownloadPdfButton quotation={q} compact testId={`quotation-download-btn-m-${q.id}`} className="-m-1" />
                              {isAdmin && (
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(q); }}
                                  className="text-slate-300 hover:text-rose-600 p-1 -m-1"
                                  data-testid={`quotation-delete-btn-m-${q.id}`}
                                  aria-label="Delete quotation"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              <ExternalLink className="w-3.5 h-3.5 text-slate-400 inline-block" />
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      <ConfirmDelete
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Delete this quotation?"
        recordLabel={deleteTarget ? `${deleteTarget.quotation_number} — ${deleteTarget.lead?.company_name || ""}` : ""}
        softDelete
        loading={deleting}
        confirmLabel="Archive quotation"
        testIdPrefix="quotation-confirm-delete"
        onConfirm={async () => {
          if (!deleteTarget) return;
          setDeleting(true);
          try {
            await api.delete(`/quotations/${deleteTarget.id}`);
            toast.success("Quotation archived — restore anytime from the Audit Log");
            setDeleteTarget(null);
            load();
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setDeleting(false); }
        }}
      />
    </div>
  );
}

// ---------- Create dialog ----------
const BLANK_ITEM = { catalog_id: "", machine_model: "", quantity: 1, unit_price: 0, discount_amount: 0 };

export function CreateQuotationDialog({ leads, catalog = [], presetLeadId = "", onCreated, onClose }) {
  const [leadId, setLeadId] = useState(presetLeadId);
  const [regionId, setRegionId] = useState("");
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState("");
  const [quoteType, setQuoteType] = useState("sale");
  const rental = isRental(quoteType);
  // Monthly rent is typed manually, so catalog list prices are cleared for rental quotes.
  const changeType = (t) => {
    if (isRental(t) !== rental) setItems((p) => p.map((it) => ({ ...it, unit_price: isRental(t) ? 0 : Number(catalog.find((c) => c.id === it.catalog_id)?.list_price) || 0 })));
    setQuoteType(t);
  };
  const [items, setItems] = useState([{ catalog_id: "", machine_model: "", quantity: 1, unit_price: 0, discount_amount: 0 }]);
  const [prefilledFrom, setPrefilledFrom] = useState(null);  // lead name while items are an untouched lead prefill
  useEffect(() => { setLeadId(presetLeadId || ""); }, [presetLeadId]);

  // Inherit region from the picked lead unless the user has explicitly chosen one.
  const selectedLead = leads.find((l) => l.id === leadId);
  useEffect(() => {
    if (leadId && selectedLead?.region_id && !regionId) {
      setRegionId(selectedLead.region_id);
    }
    // eslint-disable-next-line
  }, [leadId]);

  // Prefill line items from the lead's potential machines, only if nothing has been entered yet.
  useEffect(() => {
    const pms = selectedLead?.potential_machines || [];
    if (!catalog.length) return;
    if (!pms.length) {
      if (prefilledFrom) { setItems([{ ...BLANK_ITEM }]); setPrefilledFrom(null); }
      return;
    }
    const blank = items.every((it) => !it.catalog_id && !(it.machine_model || "").trim()
      && Number(it.quantity) === 1 && !Number(it.unit_price) && !Number(it.discount_amount));
    if (!blank && !prefilledFrom) return;
    setItems(pms.map((pm) => {
      const cat = catalog.find((c) => c.id === pm.catalog_id);
      return { catalog_id: pm.catalog_id, item_code: cat?.item_code || "", machine_model: cat?.model_name || pm.model_name || "", quantity: pm.quantity, unit_price: Number(cat?.list_price ?? pm.list_price) || 0, discount_amount: 0 };
    }));
    setPrefilledFrom(selectedLead.company_name || "lead");
    // eslint-disable-next-line
  }, [selectedLead?.id, catalog.length]);

  const subtotal = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0), [items]);
  const totalDiscount = useMemo(() => items.reduce((s, it) => {
    const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
    return s + Math.min(Math.max(0, Number(it.discount_amount) || 0), gross);
  }, 0), [items]);
  const total = Math.max(0, subtotal - totalDiscount);
  const setItem = (i, patch) => { setPrefilledFrom(null); setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it))); };
  const pickCatalog = (i, catalog_id) => {
    const cat = catalog.find((c) => c.id === catalog_id);
    if (!cat) return;
    setItem(i, { catalog_id, item_code: cat.item_code || "", machine_model: cat.model_name, unit_price: rental ? 0 : Number(cat.list_price) || 0 });
  };
  const addRow = () => { setPrefilledFrom(null); setItems((p) => [...p, { catalog_id: "", machine_model: "", quantity: 1, unit_price: 0, discount_amount: 0 }]); };
  const rm = (i) => { setPrefilledFrom(null); setItems((p) => p.filter((_, idx) => idx !== i)); };

  const submit = async (e) => {
    e.preventDefault();
    if (!leadId) { toast.error("Choose a lead"); return; }
    if (!validUntil) { toast.error("Set the valid-until date"); return; }
    if (!items.length || items.some((it) => !it.machine_model.trim())) { toast.error("Every line needs a machine model"); return; }
    try {
      const r = await api.post("/quotations", {
        lead_id: leadId,
        valid_until: validUntil,
        notes,
        discount_amount: 0,
        quote_type: quoteType,
        region_id: regionId || "",
        line_items: items.map((it) => ({ catalog_id: it.catalog_id || "", item_code: it.item_code || "", machine_model: it.machine_model, quantity: Number(it.quantity), unit_price: Number(it.unit_price), discount_amount: Number(it.discount_amount) || 0 })),
      });
      toast.success(`Quotation ${r.data.quotation_number} created`);
      onCreated?.(r.data);
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const activeLeads = leads.filter((l) => !l.is_locked);

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-quotation-dialog">
      <DialogHeader><DialogTitle>New quotation</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lead *</Label>
            <Select value={leadId} onValueChange={setLeadId} disabled={!!presetLeadId}>
              <SelectTrigger className="mt-1" data-testid="quotation-lead-select"><SelectValue placeholder="Pick a lead" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {activeLeads.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.company_name} — {l.contact_person} · {l.stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Quote type *</Label>
            <Select value={quoteType} onValueChange={changeType}>
              <SelectTrigger className="mt-1" data-testid="new-quote-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{QUOTE_TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`new-quote-type-${v}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {rental && <div className="text-[11px] text-sky-700 mt-1" data-testid="new-quote-rental-hint">Enter the monthly rent per unit (ex-GST) as the price. Lock-in, deposit, buy option and more options can be set on the next screen.</div>}
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Valid until *</Label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1" data-testid="quotation-valid-until" />
          </div>
          <div>
            <RegionSelect
              value={regionId}
              onChange={setRegionId}
              testId="quotation-region-select"
            />
            {selectedLead?.region_name && !regionId && (
              <div className="text-[11px] text-slate-500 mt-1">
                Will inherit <b>{selectedLead.region_name}</b> from lead
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line items *</Label>
            <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-line-item"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
          </div>
          {prefilledFrom && (
            <div className="mt-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1" data-testid="prefilled-from-lead-note">
              Line items pre-filled from the potential machines on <b>{prefilledFrom}</b> at catalog list price. Edit as needed.
            </div>
          )}
          <div className="mt-1 border border-slate-200 rounded-md divide-y">
            {items.map((it, i) => (
              <div key={i} data-testid={`line-item-${i}`}>
                {/* Desktop 12-col grid */}
                <div className="hidden md:grid grid-cols-12 gap-2 p-2 items-center">
                  <div className="col-span-5">
                    <Select value={it.catalog_id || ""} onValueChange={(v) => pickCatalog(i, v)}>
                      <SelectTrigger data-testid={`li-catalog-${i}`}><SelectValue placeholder="Pick a machine model" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {catalog.length === 0 && <SelectItem value="none" disabled>No catalog items — add one in Machine Catalog</SelectItem>}
                        {catalog.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.item_code ? `${c.item_code} · ` : ""}{c.model_name} — {c.manufacturer} · ₹{Number(c.list_price || 0).toLocaleString("en-IN")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <LineCode it={it} testId={`li-code-info-${i}`} />
                  </div>
                  <Input className="col-span-1 text-right" type="number" min="0" step="1" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} data-testid={`li-qty-${i}`} />
                  <Input className="col-span-3 text-right" type="number" min="0" step="0.01" placeholder={rental ? "Monthly rent / unit" : "Unit price"} value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} data-testid={`li-price-${i}`} />
                  <Input className="col-span-2 text-right" type="number" min="0" step="0.01" placeholder="Disc ₹" value={it.discount_amount ?? 0} onChange={(e) => setItem(i, { discount_amount: e.target.value })} data-testid={`li-discount-${i}`} />
                  <Button type="button" size="sm" variant="ghost" onClick={() => rm(i)} disabled={items.length === 1} className="col-span-1 text-red-500 hover:bg-red-50">✕</Button>
                </div>
                {/* Mobile stacked card */}
                <div className="md:hidden p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Item {i + 1}</span>
                    <Button type="button" size="sm" variant="ghost" onClick={() => rm(i)} disabled={items.length === 1} className="text-red-500 hover:bg-red-50 h-8 px-2">Remove</Button>
                  </div>
                  <Select value={it.catalog_id || ""} onValueChange={(v) => pickCatalog(i, v)}>
                    <SelectTrigger data-testid={`li-catalog-m-${i}`}><SelectValue placeholder="Pick a machine model" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {catalog.length === 0 && <SelectItem value="none" disabled>No catalog items — add one in Machine Catalog</SelectItem>}
                      {catalog.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.model_name} — {c.manufacturer}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Qty</Label>
                      <Input type="number" min="0" step="1" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} data-testid={`li-qty-m-${i}`} inputMode="numeric" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{rental ? "Monthly rent / unit ₹" : "Unit price ₹"}</Label>
                      <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} data-testid={`li-price-m-${i}`} inputMode="decimal" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Discount ₹</Label>
                    <Input type="number" min="0" step="0.01" value={it.discount_amount ?? 0} onChange={(e) => setItem(i, { discount_amount: e.target.value })} data-testid={`li-discount-m-${i}`} inputMode="decimal" />
                  </div>
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                    <span className="text-slate-500">Line total</span>
                    <span className="font-semibold text-slate-900">{fmtInr(Math.max(0, (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount_amount) || 0)))}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1 text-sm text-slate-600 md:max-w-xs md:ml-auto">
            <div className="flex justify-between"><span>Subtotal (gross)</span><span>{fmtInr(subtotal)}</span></div>
            <div className="flex justify-between"><span>Total discount</span><span className={totalDiscount ? "text-amber-700" : ""}>{totalDiscount ? `− ${fmtInr(totalDiscount)}` : fmtInr(0)}</span></div>
            <div className="flex justify-between text-slate-900 font-semibold text-base pt-1 border-t border-slate-200"><span>{rental ? "Monthly rental" : "Total"}</span><span data-testid="total-preview">{fmtInr(total)}{rental ? " / month" : ""}</span></div>
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</Label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="quotation-notes" />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-quotation-btn">Create draft</Button>
        </div>
      </form>
    </DialogContent>
  );
}

// ---------- By-Region grouped view ----------
const QUOTATION_STATUSES = ["Draft", "Sent", "Opened", "Approved", "Rejected", "Expired"];

function QuotationsByRegion({ rows, onOpen, STATUS_STYLES }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  const groups = {};
  (rows || []).forEach((q) => {
    const key = q.region_id || "__unassigned__";
    if (!groups[key]) groups[key] = {
      name: q.region_name || (key === "__unassigned__" ? "Unassigned Region" : "Unknown region"),
      quotations: [], statusCounts: {}, total: 0, approved: 0,
    };
    groups[key].quotations.push(q);
    groups[key].statusCounts[q.status] = (groups[key].statusCounts[q.status] || 0) + 1;
    groups[key].total += saleValue(q);
    if (q.status === "Approved") groups[key].approved += saleValue(q);
  });

  const list = Object.entries(groups)
    .map(([id, g]) => ({ id, ...g }))
    .sort((a, b) => {
      if (a.id === "__unassigned__") return 1;
      if (b.id === "__unassigned__") return -1;
      return b.quotations.length - a.quotations.length;
    });

  if (list.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg py-14 text-center text-slate-400" data-testid="quotations-byregion-empty">
        <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        No region data yet.
      </div>
    );
  }

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-3" data-testid="quotations-byregion-view">
      {list.map((g) => {
        const open = !collapsed[g.id];
        return (
          <section key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`quotations-byregion-group-${g.id}`}>
            <button
              type="button"
              onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
              data-testid={`quotations-byregion-toggle-${g.id}`}
            >
              {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
              <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {g.name}
                  <span className="text-slate-500 font-normal"> — {g.quotations.length} quotation{g.quotations.length === 1 ? "" : "s"} · {fmt(g.total)}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                  {QUOTATION_STATUSES.map((s) => (g.statusCounts[s] || 0) > 0 ? `${g.statusCounts[s]} ${s}` : null)
                    .filter(Boolean).join("  ·  ") || "No quotations"}
                  {g.approved > 0 && <span className="text-emerald-600"> · {fmt(g.approved)} won</span>}
                </div>
              </div>
            </button>
            {open && (
              <>
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/60">
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Number</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Client</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Total</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Valid until</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.quotations.map((q) => (
                        <TableRow key={q.id} onClick={() => onOpen(q)} className="hover:bg-slate-50 cursor-pointer" data-testid={`quotations-byregion-row-${q.id}`}>
                          <TableCell className="font-mono text-sm text-slate-900"><span className="inline-flex items-center gap-1.5">{q.quotation_number}<RevBadge revision={q.revision} /></span></TableCell>
                          <TableCell className="text-sm text-slate-600">{q.lead?.company_name || "—"}</TableCell>
                          <TableCell><Badge variant="outline" className={STATUS_STYLES[q.status]}>{q.status}</Badge></TableCell>
                          <TableCell className="text-right font-semibold text-slate-900"><QuoteAmount q={q} fmt={fmt} testId={`byregion-amount-${q.id}`} /></TableCell>
                          <TableCell className="text-xs text-slate-500">{q.valid_until}</TableCell>
                          <TableCell className="text-right"><DownloadPdfButton quotation={q} compact testId={`byregion-download-btn-${q.id}`} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <ul className="md:hidden divide-y divide-slate-100 border-t border-slate-100">
                  {g.quotations.map((q) => (
                    <li key={q.id} onClick={() => onOpen(q)} className="p-4 flex items-start gap-3 active:bg-slate-50" data-testid={`quotations-byregion-card-m-${q.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-slate-900">{q.quotation_number}</span>
                          <RevBadge revision={q.revision} />
                          <Badge variant="outline" className={`${STATUS_STYLES[q.status]} shrink-0 text-[10px]`}>{q.status}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 truncate">{q.lead?.company_name || "—"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold text-slate-900"><QuoteAmount q={q} fmt={fmt} testId={`byregion-amount-m-${q.id}`} /></div>
                        <DownloadPdfButton quotation={q} compact testId={`byregion-download-btn-m-${q.id}`} />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

